import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import { Card, Tag } from '@/shared/ui/primitives';
import { tEnum } from '@/shared/utils/enumTranslation';
import EntityTable from '@/shared/ui/EntityTable';
import { useRoles } from '../hooks/useRoles';
import type { AdminRoleRecord } from '../api/role.api';

export default function RolesPage() {
  const { t } = useTranslation();
  const { data = [], isLoading } = useRoles();

  return (
    <div>
      <PageHeader title={t('roles.title')} subtitle={t('roles.subtitle')} />

      <div className="ds-row" style={{ gap: 16, marginBottom: 16 }}>
        {data.map((row) => (
          <Card key={row.id} style={{ flex: '1 1 260px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
              <strong style={{ fontSize: 18 }}>{tEnum(t, 'adminRole', row.key) || row.name}</strong>
              {row.systemRole && <Tag color="info">{t('roles.systemRole')}</Tag>}
            </div>
            <div className="ds-muted" style={{ marginTop: 10, lineHeight: 1.5 }}>
              {row.description || t(`roles.descriptions.${row.key}`, { defaultValue: t('roles.descriptions.default') })}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
              <Tag>{t('roles.adminCount', { count: row.adminCount ?? 0 })}</Tag>
              <Tag>{t('roles.permissionCount', { count: row.permissions?.length ?? 0 })}</Tag>
            </div>
          </Card>
        ))}
      </div>

      <EntityTable<AdminRoleRecord>
        data={data}
        loading={isLoading}
        rowKey="id"
        page={0}
        pageSize={data.length || 20}
        total={data.length}
        onPageChange={() => undefined}
        columns={[
          {
            title: t('roles.columns.role'),
            render: (_: any, row) => (
              <div>
                <strong>{tEnum(t, 'adminRole', row.key) || row.name}</strong>
                <div className="ds-muted">{row.description || t('roles.descriptions.default')}</div>
              </div>
            ),
          },
          {
            title: t('roles.columns.type'),
            dataIndex: 'systemRole',
            render: (systemRole: boolean) => (
              <Tag color={systemRole ? 'info' : 'default'}>
                {systemRole ? t('roles.systemRole') : t('roles.customRole')}
              </Tag>
            ),
          },
          { title: t('roles.columns.admins'), dataIndex: 'adminCount' },
          {
            title: t('roles.columns.permissions'),
            render: (_: any, row) => (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(row.permissions || []).slice(0, 8).map((permission) => (
                  <Tag key={permission}>{formatPermissionLabel(permission)}</Tag>
                ))}
                {(row.permissions?.length ?? 0) > 8 && <Tag>+{row.permissions.length - 8}</Tag>}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}

function formatPermissionLabel(permission: string) {
  return permission
    .replace(/^admin:/, '')
    .replace(/[_:.]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
